// v4 structure discovery: fills structural slots (list/items/empty) from safe Webflow
// conventions when explicit data-pf attributes are absent. Explicit always wins.
// Business mappings (field names, group-to-field matching) are never inferred here —
// that is the assisted-setup flow's job (see agents/panda-filter-webflow-setup/SKILL.md).

import { DATA_LIST, DATA_ITEM, DATA_EMPTY, DATA_FILTER, DATA_FILTERS_WRAPPER } from './constants.js';
import { toArray } from './utils.js';

// A .w-dyn-list holding filter controls is a filter-option list, never a result list.
function isFilterList(dynList) {
  return !!dynList.querySelector(
    DATA_FILTER + ', .w-checkbox, .w-radio, input[type="checkbox"], input[type="radio"]'
  );
}

// A list inside an explicit [data-pf="filters-wrapper"] is a filter group, never a result
// list — even before its controls are wired (the zero-touch mid-setup case).
function isInFiltersWrapper(dynList, wrapper) {
  var region = dynList.closest(DATA_FILTERS_WRAPPER);
  return !!(region && wrapper.contains(region));
}

// Nested .w-dyn-list (e.g. a tag list inside a result card) must not count as a
// separate candidate; only top-level lists within the wrapper qualify.
function isNestedInOtherDynList(dynList, wrapper) {
  var parent = dynList.parentElement;
  while (parent && parent !== wrapper) {
    if (parent.classList && parent.classList.contains('w-dyn-list')) {
      return true;
    }
    parent = parent.parentElement;
  }
  return false;
}

export function findListCandidates(wrapper) {
  return toArray(wrapper.querySelectorAll('.w-dyn-list'))
    .filter(function (dynList) {
      return (
        !isFilterList(dynList) &&
        !isNestedInOtherDynList(dynList, wrapper) &&
        !isInFiltersWrapper(dynList, wrapper)
      );
    })
    .map(function (dynList) {
      return dynList.querySelector('.w-dyn-items');
    })
    .filter(Boolean);
}

export function discoverList(wrapper) {
  var explicit = wrapper.querySelector(DATA_LIST);
  if (explicit) {
    return { source: 'explicit', node: explicit, candidates: [explicit] };
  }

  var candidates = findListCandidates(wrapper);
  if (candidates.length === 1) {
    return { source: 'inferred', node: candidates[0], candidates: candidates };
  }

  if (!candidates.length) {
    return { source: 'missing', node: null, candidates: [] };
  }

  return { source: 'ambiguous', node: null, candidates: candidates };
}

export function discoverItems(list, listSource) {
  if (!list) {
    return { source: 'missing', selector: DATA_ITEM, count: 0 };
  }

  var explicitCount = list.querySelectorAll(DATA_ITEM).length;
  if (explicitCount) {
    return { source: 'explicit', selector: DATA_ITEM, count: explicitCount };
  }

  var inferredCount = list.querySelectorAll('.w-dyn-item').length;
  if (inferredCount || listSource === 'inferred') {
    return { source: inferredCount ? 'inferred' : 'missing', selector: '.w-dyn-item', count: inferredCount };
  }

  return { source: 'missing', selector: DATA_ITEM, count: 0 };
}

export function discoverEmpty(wrapper, list) {
  var explicit = wrapper.querySelector(DATA_EMPTY);
  if (explicit) {
    return { source: 'explicit', node: explicit };
  }

  if (list) {
    var dynList = list.closest ? list.closest('.w-dyn-list') : null;
    var inferred = dynList ? dynList.querySelector('.w-dyn-empty') : null;
    if (inferred) {
      return { source: 'inferred', node: inferred };
    }
  }

  return { source: 'missing', node: null };
}

export function describeNode(node) {
  if (!node) {
    return '(none)';
  }
  var tag = node.tagName ? node.tagName.toLowerCase() : 'node';
  var id = node.id ? '#' + node.id : '';
  var className = typeof node.className === 'string' && node.className.trim()
    ? '.' + node.className.trim().split(/\s+/).join('.')
    : '';
  return tag + id + className;
}

export function discoverStructure(wrapper) {
  var list = discoverList(wrapper);
  var item = discoverItems(list.node, list.source);
  var empty = discoverEmpty(wrapper, list.node);
  return { list: list, item: item, empty: empty };
}
